import { Component, signal, inject, computed } from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { Router, NavigationStart } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter } from 'rxjs';
import { DrawerModule } from 'primeng/drawer';
import { ButtonModule } from 'primeng/button';
import { CdkDragDrop, DragDropModule } from '@angular/cdk/drag-drop';
import { TranslateModule } from '@ngx-translate/core';
import { AccountsStore, AppStore } from '@app/core/states';
import { TranslateService } from '@ngx-translate/core';
import { SETTINGS } from '@app/domain/ports';
import { SETTINGS_REPOSITORY } from '@app/core/tokens/infrastructure.tokens';
import {
  BANK_INTEGRATION_PLUGINS,
  type BankIntegrationPlugin,
  hasEnabledBankIntegrationPlugins,
  NAV_PLUGINS,
  NavPlugin,
} from '@app/ui/shared';
import {
  addSeparator,
  MainMenuLayout,
  MenuArea,
  MenuRow,
  moveMenuItem,
  reconcileLayout,
  removeSeparator,
  renderRows,
} from './screen-actions-menu-layout';

export interface MainMenuItem {
  id: string;
  label: string;
  icon: string;
  routerLink?: string;
  disabled?: boolean;
  command?: () => void;
}

const STATIC_MOVABLE_IDS = [
  'accounts',
  'expenses',
  'incomes',
  'planned-expenses',
  'planned-incomes',
  'received-credits',
  'issued-loans',
  'currencies',
  'reports',
  'bank-imports',
  'backups',
  'categories',
] as const;

@Component({
  selector: 'app-screen-actions',
  standalone: true,
  imports: [DrawerModule, ButtonModule, DragDropModule, TranslateModule, NgTemplateOutlet],
  templateUrl: './screen-actions.component.html',
  styleUrl: './screen-actions.component.css',
})
export class ScreenActionsComponent {
  private readonly router = inject(Router);
  private readonly accountsStore = inject(AccountsStore);
  private readonly appStore = inject(AppStore);
  private readonly translate = inject(TranslateService);
  private readonly settings = inject(SETTINGS_REPOSITORY);
  private readonly bankIntegrationPlugins = (inject(BANK_INTEGRATION_PLUGINS, { optional: true }) ??
    []) as BankIntegrationPlugin[];
  private readonly navPlugins = (inject(NAV_PLUGINS, { optional: true }) ?? []) as NavPlugin[];
  private readonly knownIds = new Set<string>([
    ...STATIC_MOVABLE_IDS,
    ...this.navPlugins.map((plugin) => `plugin:${plugin.id}`),
  ]);
  private readonly defaultLayout = this.createDefaultLayout();

  readonly drawerVisible = signal(false);
  readonly editMode = signal(false);
  readonly overflowExpanded = signal(false);
  readonly dragging = signal(false);
  readonly currentUrl = signal(this.router.routerState.snapshot.url);
  readonly dropListIds = ['main-menu-top', 'main-menu-overflow', 'main-menu-bottom'];
  readonly layout = signal(
    reconcileLayout(
      this.settings.get<MainMenuLayout>(SETTINGS.MAIN_MENU_LAYOUT),
      this.knownIds,
      this.defaultLayout,
    ),
  );

  readonly menuItems = computed<MainMenuItem[]>(() => {
    this.appStore.interfaceLanguage(); // subscribe to language changes
    const hasAccounts = this.accountsStore.hasAccounts();
    const hasEnabledBankIntegrations = hasEnabledBankIntegrationPlugins(this.bankIntegrationPlugins);
    const items: MainMenuItem[] = [
      {
        id: 'accounts',
        label: this.translate.instant('navigations.accounts'),
        icon: 'pi pi-building-columns',
        routerLink: '/accounts',
      },
      {
        id: 'expenses',
        label: this.translate.instant('navigations.expenses'),
        icon: 'pi pi-arrow-right-arrow-left',
        routerLink: '/expenses',
        disabled: !hasAccounts,
      },
      {
        id: 'incomes',
        label: this.translate.instant('navigations.incomes'),
        icon: 'pi pi-arrow-down-left-and-arrow-up-right-to-center',
        routerLink: '/incomes',
        disabled: !hasAccounts,
      },
      {
        id: 'planned-expenses',
        label: this.translate.instant('navigations.planned_expenses'),
        icon: 'pi pi-calendar-minus',
        routerLink: '/planned-expenses',
        disabled: !hasAccounts,
      },
      {
        id: 'planned-incomes',
        label: this.translate.instant('navigations.planned_incomes'),
        icon: 'pi pi-calendar-plus',
        routerLink: '/planned-incomes',
        disabled: !hasAccounts,
      },
      {
        id: 'received-credits',
        label: this.translate.instant('navigations.received_credits'),
        icon: 'pi pi-money-bill',
        routerLink: '/received-credits',
        disabled: !hasAccounts,
      },
      {
        id: 'issued-loans',
        label: this.translate.instant('navigations.issued_loans'),
        icon: 'pi pi-arrow-circle-right',
        routerLink: '/issued-loans',
        disabled: !hasAccounts,
      },
      {
        id: 'currencies',
        label: this.translate.instant('navigations.currencies'),
        icon: 'pi pi-dollar',
        routerLink: '/currencies',
      },
      {
        id: 'reports',
        label: this.translate.instant('navigations.reports'),
        icon: 'pi pi-chart-line',
        routerLink: '/reports',
      },
    ];

    if (hasEnabledBankIntegrations) {
      items.push({
        id: 'bank-imports',
        label: this.translate.instant('navigations.import'),
        icon: 'pi pi-download',
        routerLink: '/bank-imports',
      });
    }

    // Add generic nav plugin items
    for (const plugin of this.navPlugins) {
      if (plugin.visible()) {
        items.push({
          id: `plugin:${plugin.id}`,
          label: plugin.label(),
          icon: plugin.icon,
          command: plugin.command,
        });
      }
    }

    items.push(
      {
        id: 'backups',
        label: this.translate.instant('navigations.backup'),
        icon: 'pi pi-cloud',
        routerLink: '/backups',
      },
      {
        id: 'categories',
        label: this.translate.instant('navigations.manage_categories'),
        icon: 'pi pi-sitemap',
        routerLink: '/categories',
      },
    );

    return items;
  });

  readonly itemMap = computed(() => new Map(this.menuItems().map((item) => [item.id, item])));
  readonly liveIds = computed(() => new Set(this.menuItems().map((item) => item.id)));
  readonly renderedAreas = computed<Record<MenuArea, MenuRow[]>>(() => {
    const layout = this.layout();
    const liveIds = this.liveIds();
    return {
      top: renderRows(layout.top, liveIds),
      overflow: renderRows(layout.overflow, liveIds),
      bottom: renderRows(layout.bottom, liveIds),
    };
  });
  readonly overflowContainsActiveRoute = computed(() =>
    this.renderedAreas().overflow.some((row) => {
      const item = this.itemMap().get(row.id);
      return !!item?.routerLink && this.isRouteActive(item.routerLink);
    }),
  );

  constructor() {
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationStart),
        takeUntilDestroyed(),
      )
      .subscribe((event) => {
        this.currentUrl.set(event.url);
        this.closeMenu();
      });
  }

  openMenu(): void {
    this.editMode.set(false);
    this.overflowExpanded.set(this.overflowContainsActiveRoute());
    this.drawerVisible.set(true);
  }

  closeMenu(): void {
    this.editMode.set(false);
    this.overflowExpanded.set(false);
    this.dragging.set(false);
    this.drawerVisible.set(false);
  }

  onDrawerVisibleChange(visible: boolean): void {
    if (visible) this.drawerVisible.set(true);
    else this.closeMenu();
  }

  renderedRows(area: MenuArea): MenuRow[] {
    return this.renderedAreas()[area];
  }

  itemById(id: string | undefined): MainMenuItem | undefined {
    return id ? this.itemMap().get(id) : undefined;
  }

  setEditMode(enabled: boolean): void {
    this.editMode.set(enabled);
    if (enabled) this.overflowExpanded.set(true);
  }

  toggleOverflow(): void {
    const expanded = !this.overflowExpanded();
    this.overflowExpanded.set(expanded);
    // The edit switch lives inside the expandable body, so collapsing must not leave edit mode stuck on.
    if (!expanded) this.editMode.set(false);
  }

  activateItem(item: MainMenuItem): void {
    if (this.editMode() || item.disabled) return;

    if (item.routerLink) {
      void this.router.navigateByUrl(item.routerLink);
    } else {
      item.command?.();
    }
    this.closeMenu();
  }

  activateSettings(): void {
    if (this.editMode()) return;
    void this.router.navigateByUrl('/settings');
    this.closeMenu();
  }

  isRouteActive(routerLink: string): boolean {
    const url = this.currentUrl().split(/[?#]/, 1)[0];
    return url === routerLink || url.startsWith(`${routerLink}/`);
  }

  onDrop(event: CdkDragDrop<MenuArea>, destination: MenuArea): void {
    const itemId = event.item.data as string;
    const next = moveMenuItem(
      this.layout(),
      itemId,
      destination,
      event.currentIndex,
      this.liveIds(),
      this.knownIds,
    );
    this.saveLayout(next);
  }

  toggleDivider(area: MenuArea, row: MenuRow): void {
    if (!this.editMode()) return;

    const current = this.layout();
    const next =
      row.separatorAfter === null
        ? addSeparator(current, area, row.id)
        : removeSeparator(current, area, row.separatorAfter);

    if (next !== current) this.saveLayout(next);
  }

  hasRenderedItems(area: MenuArea): boolean {
    return this.renderedRows(area).length > 0;
  }

  private saveLayout(layout: MainMenuLayout): void {
    this.layout.set(layout);
    this.settings.set(SETTINGS.MAIN_MENU_LAYOUT, layout);
  }

  private createDefaultLayout(): MainMenuLayout {
    const item = (id: string) => ({ id });
    const separator = () => ({ separator: true }) as const;

    return {
      top: [
        item('accounts'),
        item('expenses'),
        item('incomes'),
        separator(),
        item('planned-expenses'),
        item('planned-incomes'),
        separator(),
        item('received-credits'),
        item('issued-loans'),
        separator(),
        item('currencies'),
        separator(),
        item('reports'),
        separator(),
      ],
      overflow: [],
      bottom: [
        item('bank-imports'),
        ...this.navPlugins.map((plugin) => item(`plugin:${plugin.id}`)),
        item('backups'),
        separator(),
        item('categories'),
        separator(),
      ],
    };
  }
}
